import { useState, useEffect } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from "react-toastify";

import { useAuth } from "../../../hooks/useAuth";
import { useTenant } from "../../../hooks/useTenant";
import { updateProfile, updatePassword } from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db, auth } from "../../../services/firebase";
import StorageService from "../../../services/storage_service";
import { usePlan } from "../../../hooks/usePlan";
import { MaskHelper, AddressHelper } from "../../../utils/helpers";

export const useUserProfile = () => {
    const { user: authUser } = useAuth();
    const { planId } = usePlan();
    const { idTenant } = useTenant();
    const isPersonal = planId === "personal";

    const [initialLoading, setInitialLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [cepLoading, setCepLoading] = useState(false);

    const [profileData, setProfileData] = useState({
        firstName: "",
        lastName: "",
        phone: "",
        gender: "",
        birthDate: "",
        specialization: "",
        photoUrl: "",
        zipCode: "",
        street: "",
        number: "",
        neighborhood: "",
        city: "",
        state: "",
        password: "",
        confirmPassword: ""
    });

    useEffect(() => {
        const fetchProfileData = async () => {
            if (authUser?.uid) {
                setInitialLoading(true);
                try {
                    let displayName = authUser.displayName || "";
                    let fName = "";
                    let lName = "";
                    if (displayName.includes(" ")) {
                        fName = displayName.substring(0, displayName.indexOf(" "));
                        lName = displayName.substring(displayName.indexOf(" ") + 1);
                    } else {
                        fName = displayName;
                    }

                    setProfileData(prev => ({
                        ...prev,
                        firstName: fName,
                        lastName: lName,
                        photoUrl: authUser.photoURL || ""
                    }));

                    const userDocRef = doc(db, "users", authUser.uid);
                    const snap = await getDoc(userDocRef);
                    if (snap.exists()) {
                        const data = snap.data();
                        setProfileData(prev => ({
                            ...prev,
                            firstName: data.firstName || fName,
                            lastName: data.lastName || lName,
                            phone: data.phone || "",
                            gender: data.gender || "",
                            birthDate: data.birthDate || "",
                            specialization: data.specialization || "",
                            photoUrl: data.photoUrl || data.photoURL || data.photo || authUser.photoURL || "",
                            zipCode: data.address?.zipCode || "",
                            street: data.address?.street || "",
                            number: data.address?.number || "",
                            neighborhood: data.address?.neighborhood || "",
                            city: data.address?.city || "",
                            state: data.address?.state || ""
                        }));
                    }
                } catch (error) {
                    console.error("Erro ao puxar perfil:", error);
                    toast.error("Erro ao carregar dados do perfil.");
                } finally {
                    setInitialLoading(false);
                }
            }
        };
        fetchProfileData();
    }, [authUser]);

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: profileData,
        validationSchema: Yup.object({
            firstName: Yup.string().required("O Nome é obrigatório"),
            lastName: Yup.string().required("O Sobrenome é obrigatório"),
            phone: Yup.string(),
            gender: Yup.string(),
            birthDate: Yup.string()
                .nullable()
                .transform(value => (value === "" ? null : value))
                .test("valid-date", "Data de nascimento inválida", (value) => {
                    if (!value) return true;
                    return /^\d{4}-\d{2}-\d{2}$/.test(value);
                }),
            zipCode: Yup.string(),
            street: Yup.string(),
            number: Yup.string(),
            neighborhood: Yup.string(),
            city: Yup.string(),
            state: Yup.string(),
            specialization: Yup.string(),
            password: Yup.string()
                .min(6, "A nova senha deve ter pelo menos 6 caracteres")
                .notRequired(),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref('password'), null], "As senhas não coincidem")
                .when("password", {
                    is: (val) => val && val.length > 0,
                    then: (schema) => schema.required("Confirme sua nova senha"),
                    otherwise: (schema) => schema.notRequired()
                })
        }),
        onSubmit: async (values) => {
            const currentUser = auth.currentUser;
            if (!currentUser || !authUser?.uid) return;
            setSaving(true);

            try {
                const fullName = `${values.firstName} ${values.lastName}`.trim();
                let finalPhotoUrl = values.photoUrl;

                if (values.photoUrl && values.photoUrl.startsWith("data:image")) {
                    const storagePath = `users/${authUser.uid}/profile.jpg`;
                    finalPhotoUrl = await StorageService.uploadBase64Image(storagePath, values.photoUrl);
                }

                await updateProfile(currentUser, {
                    displayName: fullName,
                    photoURL: finalPhotoUrl || currentUser.photoURL
                });

                const userDocRef = doc(db, "users", authUser.uid);
                const updatePayload = {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    fullName: fullName,
                    phone: values.phone,
                    gender: values.gender,
                    birthDate: values.birthDate,
                    address: {
                        zipCode: values.zipCode,
                        street: values.street,
                        number: values.number,
                        neighborhood: values.neighborhood,
                        city: values.city,
                        state: values.state
                    },
                    photoUrl: finalPhotoUrl || null,
                    photoURL: finalPhotoUrl || null,
                    photo: finalPhotoUrl || null,
                };

                if (isPersonal) {
                    updatePayload.specialization = values.specialization || null;
                }

                await updateDoc(userDocRef, updatePayload);

                // Sincronizar foto e nome para o tenant (logoUrl) se for Personal
                if (isPersonal && idTenant) {
                    try {
                        const tenantDocRef = doc(db, "tenants", idTenant);
                        await updateDoc(tenantDocRef, {
                            logoUrl: finalPhotoUrl || null,
                            name: fullName,
                        });
                    } catch (syncErr) {
                        console.warn("Erro ao sincronizar foto com tenant:", syncErr);
                    }
                }

                if (values.password && values.password.length >= 6) {
                    try {
                        await updatePassword(currentUser, values.password);
                        toast.success("Perfil e senha atualizados com sucesso!");
                        setTimeout(() => window.location.reload(), 1500);
                        return;
                    } catch (authErr) {
                        console.error("Erro ao alterar senha:", authErr);
                        if (authErr.code === "auth/requires-recent-login") {
                            toast.error("Por segurança, saia e faça login novamente para trocar a sua senha.");
                        } else {
                            toast.error("Erro ao alterar a senha: " + authErr.message);
                        }
                        setSaving(false);
                        return;
                    }
                }

                toast.success("Perfil atualizado com sucesso!");
                setTimeout(() => window.location.reload(), 1500);

            } catch (err) {
                console.error("Erro ao atualizar perfil:", err);
                toast.error("Erro ao atualizar perfil: " + err.message);
            } finally {
                setSaving(false);
            }
        }
    });

    const handleCepChange = async (e) => {
        let cepVal = e.target.value;
        cepVal = MaskHelper.cep(cepVal);
        validation.setFieldValue("zipCode", cepVal);

        const cleanCep = cepVal.replace(/\D/g, "");
        if (cleanCep.length === 8) {
            setCepLoading(true);
            try {
                const address = await AddressHelper.fetchByCep(cleanCep);
                if (address) {
                    validation.setFieldValue("street", address.street);
                    validation.setFieldValue("neighborhood", address.neighborhood);
                    validation.setFieldValue("city", address.city);
                    validation.setFieldValue("state", address.state);
                }
            } catch (error) {
                toast.error("Erro ao buscar CEP");
            } finally {
                setCepLoading(false);
            }
        }
    };

    return {
        state: {
            initialLoading,
            saving,
            cepLoading,
            authUser,
            isPersonal
        },
        validation,
        actions: {
            handleCepChange
        }
    };
};
